import React, { memo } from 'react';
import { Trash2, Plus, Package } from 'lucide-react';
import type { DetalleFacturaCompra } from '../../types/facturas';
import {
  capitalizarIngrediente,
  capitalizarMarca,
  formatearPrecioConPuntos,
} from '../../utils/facturaFormatters';
import InputPrecioFormateado from './InputPrecioFormateado';

// Tipos de contenedor para el selector de nivel 1
const TIPOS_CONTENEDOR = ['Caja', 'Paquete', 'Display', 'Bolsa', 'Bulto', 'Saco'];

interface TablaIngredientesProps {
  detalles: DetalleFacturaCompra[];
  categorias: any[];
  unidadesMedida: any[];
  onUpdateDetalle: (index: number, field: string, value: any) => void;
  onEliminarDetalle: (index: number) => void;
  onAbrirNuevaCategoria: (index: number) => void;
  onAbrirNuevaUnidadMedida: (index: number) => void;
}

// Aplica múltiples actualizaciones de campos en un solo batch
function aplicarUpdates(
  onUpdate: (idx: number, field: string, val: any) => void,
  index: number,
  updates: Partial<DetalleFacturaCompra>
) {
  Object.entries(updates).forEach(([field, value]) => {
    onUpdate(index, field, value);
  });
}

/**
 * Recalcula todos los campos derivados de la jerarquía.
 * Recibe el detalle actual + cambios parciales y devuelve el objeto
 * con todos los campos actualizados (cantidad total, precios, subtotal, descripción).
 *
 * Fórmulas (precio_nivel1 siempre es el precio TOTAL del grupo completo):
 *   Nivel 2: precio_unitario = precio_grupo_total / (n1qty × n1units)
 *            subtotal = precio_unitario × (n1qty × n1units) = precio_grupo_total
 *   Nivel 3: precio_unitario = precio_grupo_total / (n1qty × n1units × n2base)
 *            subtotal = precio_unitario × (n1qty × n1units × n2base) = precio_grupo_total
 *            precio_nivel2 (precio por caja) = precio_grupo_total / n1qty
 */
function recalcularJerarquia(
  detalle: DetalleFacturaCompra,
  cambios: Partial<DetalleFacturaCompra>
): Partial<DetalleFacturaCompra> {
  const d = { ...detalle, ...cambios };
  const nivel = d.jerarquia_niveles || 2;
  const n1qty = d.nivel1_cantidad || 1;
  const n1units = d.nivel1_unidades || 1;
  const n2base = d.nivel2_cantidad_base || 1;
  const precioN1 = d.precio_nivel1 || 0;

  let cantidadTotal: number;
  let precioBase: number;
  let extras: Partial<DetalleFacturaCompra> = {};

  // Unidad de medida real del ingrediente (de la tabla maestra, no del OCR).
  // Se lee directamente del detalle combinado para mostrarla desde el inicio
  // sin necesidad de que el usuario interactúe con el select de unidad.
  const unidadReal = d.unidad_medida?.trim() || '';

  if (nivel === 2) {
    // Nivel 2: precio_por_grupo = precio total pagado por todos los contenedores
    // precio_unitario = precio_por_grupo / (n1qty × n1units)
    // subtotal = precio_unitario × (n1qty × n1units) = precio_por_grupo
    cantidadTotal = n1qty * n1units;
    precioBase = cantidadTotal > 0 ? precioN1 / cantidadTotal : 0;
    const unidadLabel = unidadReal || '[unidad]';
    extras.descripcion_jerarquia =
      `${n1qty} unidades de ${n1units} ${unidadLabel}`;
  } else {
    // Nivel 3: precio_por_grupo = precio total de todos los contenedores
    // precio_unitario = precio_por_grupo / (contenedores × unidades × medida)
    //                 = precioN1 / (n1qty × n1units × n2base)
    // subtotal = precio_unitario × cantidad_total = precioN1
    cantidadTotal = n1qty * n1units * n2base;
    precioBase = cantidadTotal > 0 ? precioN1 / cantidadTotal : 0;
    // precio_nivel2 = precio por unidad de nivel 2 (ej: precio de UNA caja)
    // = precioN1 / n1qty
    const precioN2 = n1qty > 0 ? precioN1 / n1qty : 0;
    extras.precio_nivel2 = precioN2;
    const unidadLabel = unidadReal || '[unidad]';
    extras.descripcion_jerarquia =
      `${n1qty} ${d.nivel1_nombre || 'Caja'} con ${n1units} unidades de ${n2base} ${unidadLabel}`;
  }

  return {
    ...cambios,
    ...extras,
    cantidad: cantidadTotal,
    precio_unitario: precioBase,
    precio_base: precioBase,
    subtotal: precioBase * cantidadTotal,
    es_pack: true,
    // Campos legacy para compatibilidad
    cantidad_packs: n1qty,
    unidades_por_pack: n1units,
    precio_pack: precioN1,
    formato_compra: extras.descripcion_jerarquia,
  };
}

// ─────────────────────────────────────────────
// Sub-fila de agrupamiento (se muestra debajo)
// ─────────────────────────────────────────────
interface FilaGrupoProps {
  detalle: DetalleFacturaCompra;
  index: number;
  unidadesMedida: any[];
  onUpdateDetalle: (index: number, field: string, value: any) => void;
  onCambiarNivel: (nivel: 2 | 3) => void;
}

const FilaGrupo: React.FC<FilaGrupoProps> = ({
  detalle,
  index,
  unidadesMedida,
  onUpdateDetalle,
  onCambiarNivel,
}) => {
  const nivel = (detalle.jerarquia_niveles || 2) as 2 | 3;
  // Leer la unidad directamente del detalle. Si está vacía, usar el símbolo
  // de la primera unidad disponible para que se muestre desde el inicio
  // sin necesidad de que el usuario interactúe con el select de unidad.
  const primeraUnidad = unidadesMedida[0]?.simbolo || '';
  const unidad = detalle.unidad_medida?.trim() || primeraUnidad;

  const handleCampo = (campo: string, valor: any) => {
    // Al recalcular, inyectamos la unidad efectiva para que descripcion_jerarquia
    // siempre incluya la unidad sin necesidad de que el usuario cambie el select.
    const detalleConUnidad = unidad !== detalle.unidad_medida
      ? { ...detalle, unidad_medida: unidad }
      : detalle;
    const updates = recalcularJerarquia(detalleConUnidad, { [campo]: valor, unidad_medida: unidad });
    aplicarUpdates(onUpdateDetalle, index, updates);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Ícono + etiqueta */}
      <Package className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
      <span className="text-xs font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap">
        Agrupamiento:
      </span>

      {/* Selector de nivel */}
      <select
        value={nivel}
        onChange={(e) => onCambiarNivel(Number(e.target.value) as 2 | 3)}
        className="px-2 py-0.5 border border-blue-200 dark:border-blue-700 rounded text-xs
                   bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 cursor-pointer
                   focus:outline-none focus:ring-1 focus:ring-blue-400"
        aria-label="Nivel de agrupamiento"
      >
        <option value={2}>2 niveles: unidades → {unidad}</option>
        <option value={3}>3 niveles: contenedor → unidades → {unidad}</option>
      </select>

      {/* ── Cantidad nivel 1 ── */}
      <input
        type="number"
        value={detalle.nivel1_cantidad ?? 1}
        onChange={(e) => handleCampo('nivel1_cantidad', parseFloat(e.target.value) || 1)}
        min="0.01"
        step="0.01"
        className="w-14 px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-sm
                   text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                   focus:outline-none focus:ring-1 focus:ring-blue-400"
        aria-label={nivel === 3 ? 'Cantidad de contenedores' : 'Cantidad de grupos'}
      />

      {/* ── Tipo de contenedor: solo en nivel 3 ── */}
      {nivel === 3 && (
        <select
          value={detalle.nivel1_nombre || 'Caja'}
          onChange={(e) => handleCampo('nivel1_nombre', e.target.value)}
          className="px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-sm
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer
                     focus:outline-none focus:ring-1 focus:ring-blue-400"
          aria-label="Tipo de contenedor"
        >
          {TIPOS_CONTENEDOR.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      )}

      {/* ── Nivel 3: "con [X] unidades de" / Nivel 2: "unidades de" ── */}
      {nivel === 3 ? (
        <>
          <span className="text-xs text-gray-400 dark:text-gray-500">con</span>
          <input
            type="number"
            value={detalle.nivel1_unidades ?? 1}
            onChange={(e) => handleCampo('nivel1_unidades', parseFloat(e.target.value) || 1)}
            min="0.01"
            step="0.01"
            className="w-14 px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-sm
                       text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       focus:outline-none focus:ring-1 focus:ring-blue-400"
            aria-label="Unidades por contenedor"
          />
          <span className="text-xs text-gray-400 dark:text-gray-500">unidades de</span>
        </>
      ) : (
        <span className="text-xs text-gray-400 dark:text-gray-500">unidades de</span>
      )}

      {/* ── Cantidad base (gramos/ml/etc. por unidad) ── */}
      <input
        type="number"
        value={nivel === 3 ? (detalle.nivel2_cantidad_base ?? 1) : (detalle.nivel1_unidades ?? 1)}
        onChange={(e) => {
          const campo = nivel === 3 ? 'nivel2_cantidad_base' : 'nivel1_unidades';
          handleCampo(campo, parseFloat(e.target.value) || 1);
        }}
        min="0.01"
        step="0.01"
        className="w-14 px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-sm
                   text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                   focus:outline-none focus:ring-1 focus:ring-blue-400"
        aria-label={`${unidad} por unidad`}
      />

      {/* Unidad de medida base */}
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{unidad}</span>

      {/* ── Separador visual ── */}
      <span className="text-gray-200 dark:text-gray-600 select-none">|</span>

      {/* ── Precio total del grupo ── */}
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        $ grupo total:
      </span>
      <InputPrecioFormateado
        value={detalle.precio_nivel1 ?? 0}
        onChange={(value) => handleCampo('precio_nivel1', value)}
        className="w-20 text-xs"
        ariaLabel="Precio total del grupo"
      />

      {/* ── Badge resumen calculado ── */}
      {detalle.cantidad > 0 && detalle.precio_unitario > 0 && (
        <span className="ml-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 border border-blue-200
                         dark:border-blue-700 rounded-full text-xs text-blue-700 dark:text-blue-300
                         whitespace-nowrap font-medium">
          {detalle.cantidad.toLocaleString('es-CL', { maximumFractionDigits: 2 })} {unidad}
          {' · '}
          ${formatearPrecioConPuntos(detalle.precio_unitario)}/{unidad}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Fila principal del ingrediente
// ─────────────────────────────────────────────
const FilaIngrediente = memo<{
  detalle: DetalleFacturaCompra;
  index: number;
  categorias: any[];
  unidadesMedida: any[];
  onUpdateDetalle: (index: number, field: string, value: any) => void;
  onEliminarDetalle: (index: number) => void;
  onAbrirNuevaCategoria: (index: number) => void;
  onAbrirNuevaUnidadMedida: (index: number) => void;
}>(
  ({
    detalle,
    index,
    categorias,
    unidadesMedida,
    onUpdateDetalle,
    onEliminarDetalle,
    onAbrirNuevaCategoria,
    onAbrirNuevaUnidadMedida,
  }) => {
    const nivelActual = detalle.jerarquia_niveles || 1;
    const esGrupo = nivelActual >= 2;

    // Resuelve la unidad de medida efectiva: usa la del detalle si existe,
    // o la primera de la lista maestra para garantizar que siempre haya un
    // valor visible desde el inicio (punto 3 y 4 del requerimiento).
    const unidadEfectiva = detalle.unidad_medida?.trim() || unidadesMedida[0]?.simbolo || '';

    // Activar/desactivar modo grupo
    const handleToggleGrupo = (checked: boolean) => {
      if (checked) {
        const cantidadActual = detalle.cantidad || 1;
        // En nivel 2 el precio_nivel1 = precio total del grupo
        // (precio unitario × cantidad de unidades en el grupo)
        const precioContenedor = (detalle.precio_unitario || 0) * cantidadActual;
        const updates = recalcularJerarquia(
          // Si unidad_medida está vacío, inyectamos la primera disponible para
          // que recalcularJerarquia incluya la unidad en descripcion_jerarquia
          { ...detalle, unidad_medida: unidadEfectiva },
          {
            jerarquia_niveles: 2,
            nivel1_cantidad: 1,
            nivel1_nombre: 'Caja',
            nivel1_unidades: cantidadActual,
            nivel2_cantidad_base: null,
            nivel2_nombre: null,
            precio_nivel1: precioContenedor,
            unidad_medida: unidadEfectiva,
          }
        );
        aplicarUpdates(onUpdateDetalle, index, updates);
      } else {
        // Restaurar modo simple, mantener cantidad y precio calculados
        aplicarUpdates(onUpdateDetalle, index, {
          jerarquia_niveles: 1,
          es_pack: false,
          nivel1_cantidad: null,
          nivel1_nombre: null,
          nivel1_unidades: null,
          nivel2_nombre: null,
          nivel2_cantidad_base: null,
          precio_nivel1: null,
          precio_nivel2: null,
          precio_base: detalle.precio_unitario,
          descripcion_jerarquia: null,
          subtotal: detalle.precio_unitario * detalle.cantidad,
        });
      }
    };

    // Cambiar entre nivel 2 y 3
    const handleCambiarNivel = (nuevoNivel: 2 | 3) => {
      const updates = recalcularJerarquia(
        { ...detalle, unidad_medida: unidadEfectiva },
        {
          jerarquia_niveles: nuevoNivel,
          nivel2_cantidad_base: nuevoNivel === 3 ? (detalle.nivel2_cantidad_base || 1) : null,
          unidad_medida: unidadEfectiva,
        }
      );
      aplicarUpdates(onUpdateDetalle, index, updates);
    };

    return (
      <>
        {/* ── Fila principal ── */}
        <tr className="border-t border-gray-200 dark:border-gray-700">
          {/* Ingrediente */}
          <td className="px-3 py-2">
            <input
              type="text"
              value={detalle.nombre_ingrediente_temporal}
              onChange={(e) =>
                onUpdateDetalle(
                  index,
                  'nombre_ingrediente_temporal',
                  capitalizarIngrediente(e.target.value)
                )
              }
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         focus:outline-none focus:ring-1 focus:ring-blue-400"
              aria-label={`Nombre del ingrediente ${index + 1}`}
            />
          </td>

          {/* Marca */}
          <td className="px-3 py-2">
            <input
              type="text"
              value={detalle.marca || ''}
              onChange={(e) =>
                onUpdateDetalle(
                  index,
                  'marca',
                  e.target.value ? capitalizarMarca(e.target.value) : null
                )
              }
              placeholder="genérica"
              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         focus:outline-none focus:ring-1 focus:ring-blue-400"
              aria-label={`Marca del ingrediente ${index + 1}`}
            />
          </td>

          {/* Categoría */}
          <td className="px-3 py-2">
            <div className="flex gap-1">
              <select
                value={detalle.categoria_nombre || 'Otros'}
                onChange={(e) => {
                  const cat = categorias.find((c) => c.nombre === e.target.value);
                  onUpdateDetalle(index, 'categoria_nombre', e.target.value);
                  onUpdateDetalle(index, 'categoria_id', cat?.id || null);
                }}
                className="flex-1 min-w-0 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-1 focus:ring-blue-400"
                aria-label={`Categoría del ingrediente ${index + 1}`}
              >
                {categorias.map((cat: any) => (
                  <option key={cat.id} value={cat.nombre}>
                    {cat.nombre}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onAbrirNuevaCategoria(index)}
                className="flex-shrink-0 px-2 py-1 bg-blue-600 dark:bg-blue-500 text-white rounded
                           hover:bg-blue-700 dark:hover:bg-blue-600 cursor-pointer"
                title="Crear nueva categoría"
                aria-label="Crear nueva categoría"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </td>

          {/* Unidad de Medida */}
          <td className="px-3 py-2">
            <div className="flex gap-1">
              <select
                value={detalle.unidad_medida}
                onChange={(e) => {
                  if (esGrupo) {
                    const updates = recalcularJerarquia(detalle, { unidad_medida: e.target.value });
                    aplicarUpdates(onUpdateDetalle, index, updates);
                  } else {
                    onUpdateDetalle(index, 'unidad_medida', e.target.value);
                  }
                }}
                className="flex-1 min-w-0 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-1 focus:ring-blue-400"
                aria-label={`Unidad de medida del ingrediente ${index + 1}`}
              >
                {unidadesMedida.map((u) => (
                  <option key={u.id} value={u.simbolo}>
                    {u.nombre} ({u.simbolo})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onAbrirNuevaUnidadMedida(index)}
                className="flex-shrink-0 px-2 py-1 bg-blue-600 dark:bg-blue-500 text-white rounded
                           hover:bg-blue-700 dark:hover:bg-blue-600 cursor-pointer"
                title="Crear nueva unidad de medida"
                aria-label="Crear nueva unidad de medida"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </td>

          {/* Cantidad + Toggle Grupo */}
          <td className="px-3 py-2">
            <div className="flex items-center gap-2">
              {/* Input de cantidad: editable en modo simple, readonly en modo grupo */}
              <input
                type="number"
                value={detalle.cantidad}
                onChange={(e) => {
                  if (!esGrupo) {
                    const val = parseFloat(e.target.value) || 0;
                    onUpdateDetalle(index, 'cantidad', val);
                    onUpdateDetalle(index, 'subtotal', val * (detalle.precio_unitario || 0));
                  }
                }}
                readOnly={esGrupo}
                min="0"
                step="0.01"
                className={`w-16 px-2 py-1 border rounded text-sm text-center transition-colors
                  ${
                    esGrupo
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400'
                  }`}
                aria-label={`Cantidad del ingrediente ${index + 1}`}
              />

              {/* Checkbox Grupo de items */}
              <label
                className="flex items-center gap-1 cursor-pointer select-none group"
                title="Activar para ingresar el producto agrupado en cajas, paquetes o displays"
              >
                <input
                  type="checkbox"
                  checked={esGrupo}
                  onChange={(e) => handleToggleGrupo(e.target.checked)}
                  className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                  aria-label={`Activar grupo de items para ingrediente ${index + 1}`}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
                  Grupo
                </span>
              </label>
            </div>
          </td>

          {/* Precio Unitario: editable en modo simple, calculado en modo grupo */}
          <td className="px-3 py-2 text-right">
            {esGrupo ? (
              <div className="text-right leading-tight">
                <div className="text-sm font-mono text-gray-700 dark:text-gray-300">
                  ${formatearPrecioConPuntos(detalle.precio_unitario)}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  /{detalle.unidad_medida}
                </div>
              </div>
            ) : (
              <InputPrecioFormateado
                value={detalle.precio_unitario}
                onChange={(value) => {
                  onUpdateDetalle(index, 'precio_unitario', value);
                  onUpdateDetalle(index, 'subtotal', detalle.cantidad * value);
                }}
                className="w-20"
                ariaLabel={`Precio unitario del ingrediente ${index + 1}`}
              />
            )}
          </td>

          {/* Subtotal */}
          <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
            ${detalle.subtotal.toLocaleString('es-CL')}
          </td>

          {/* Eliminar */}
          <td className="px-3 py-2 text-center">
            <button
              type="button"
              onClick={() => onEliminarDetalle(index)}
              className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300
                         cursor-pointer transition-colors"
              title="Eliminar ingrediente"
              aria-label={`Eliminar ingrediente ${index + 1}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </td>
        </tr>

        {/* ── Sub-fila de agrupamiento (sólo visible cuando esGrupo = true) ── */}
        {esGrupo && (
          <tr className="border-b border-blue-100 dark:border-blue-800/50 bg-blue-50/30 dark:bg-blue-900/10">
            <td
              colSpan={8}
              className="px-4 py-2"
            >
              <FilaGrupo
                detalle={detalle}
                index={index}
                unidadesMedida={unidadesMedida}
                onUpdateDetalle={onUpdateDetalle}
                onCambiarNivel={handleCambiarNivel}
              />
            </td>
          </tr>
        )}
      </>
    );
  }
);

FilaIngrediente.displayName = 'FilaIngrediente';

// ─────────────────────────────────────────────
// Tabla principal
// ─────────────────────────────────────────────
const TablaIngredientes: React.FC<TablaIngredientesProps> = ({
  detalles,
  categorias,
  unidadesMedida,
  onUpdateDetalle,
  onEliminarDetalle,
  onAbrirNuevaCategoria,
  onAbrirNuevaUnidadMedida,
}) => {
  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-x-auto">
      <div className="min-w-[1100px]">
        <table className="w-full" role="table" aria-label="Ingredientes de la factura">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[22%]">
                Ingrediente
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[10%]">
                Marca
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[15%]">
                Categoría
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[13%]">
                Unidad
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[14%]">
                Cantidad
              </th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[11%]">
                Precio Unit.
              </th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-[10%]">
                Subtotal
              </th>
              <th className="px-3 py-2 w-[5%]" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {detalles.map((detalle, index) => (
              <FilaIngrediente
                key={index}
                detalle={detalle}
                index={index}
                categorias={categorias}
                unidadesMedida={unidadesMedida}
                onUpdateDetalle={onUpdateDetalle}
                onEliminarDetalle={onEliminarDetalle}
                onAbrirNuevaCategoria={onAbrirNuevaCategoria}
                onAbrirNuevaUnidadMedida={onAbrirNuevaUnidadMedida}
              />
            ))}
            {detalles.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm"
                >
                  No hay ingredientes. Usa el buscador o el botón "Nuevo" para agregar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(TablaIngredientes);
